import DateInput from "@/components/common/form/date-input";
import Input from "@/components/common/form/input";
import React, { useEffect } from "react";
import Range from "@/components/common/form/range";
import { authServices } from "services/auth.services";
import { useSwrGet } from "@/utils/use-swr-get";
import Select from "@/components/common/form/select";
import Button from "@/components/common/form/button";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { readFarms, setFarmData } from "@/redux/actions/farms";
import { navigationServices } from "services/navigation.service";
import {
  readTreeFields,
  readTreeGeoFields,
  setTreeFieldData,
} from "@/redux/actions/tree/fields";
import {
  readPivotFields,
  readPivotGeoFields,
  setCropFieldData,
} from "@/redux/actions/pivot/fields";

type Reducer = {
  farmsReducer: any;
};

function pumpForm({ type, icon, passProps, ...props }) {
  const { t, i18n } = useTranslation();
  const {
    data: farmData,
    isLoading: farmIsLoading,
    isError: farmIsError,
  } = useSwrGet(
    `/${type}/all`,
    process.env.NEXT_PUBLIC_BASE_URL +
      `farms/show/${type}/${authServices.userValue.org_id}`
  );
  const {
    data: suppliers,
    isLoading: suppliersIsLoading,
    isError: suppliersIsError,
  } = useSwrGet(
    `/suppliers/all`,
    process.env.NEXT_PUBLIC_BASE_URL +
      `suppliers/?org_id__id=${authServices.userValue.org_id}`
  );
  const dispatch = useDispatch();

  const { farms, currentType, readable } = useSelector(
    (state: Reducer) => state.farmsReducer
  );
  const { field, geo_field, fields, geo_fields } = useSelector(
    (state: Reducer) => state[`${currentType}FieldsReducer`]
  );

  useEffect(() => {
    if (!readable) {
      dispatch(readFarms());
    }
  }, [readable]);

  return (
    <>
      <div className="w-80">
        {" "}
        <Input
          name="item_code"
          label={t("PumpCode")}
          icon={() => (
            <p className="px-2 pt-4 text-sm font-bold outline-none appearance-none text-mako">
              {icon}
            </p>
          )}
          {...props}
        />
        <Select
          label={t("Select Farm")}
          name="farm_id"
          onChange={async (e) => {
            let selectedFarm = await navigationServices.getFarm(e.target.value);
            if (selectedFarm) {
              dispatch(
                setFarmData({
                  key: "farm",
                  value: selectedFarm?.data,
                })
              );
            }

            if (currentType === "tree") {
              dispatch(readTreeFields(`?farm_id__id=${e.target.value}`));
              dispatch(readTreeGeoFields(e.target.value));
            } else if (currentType === "crop") {
              dispatch(readPivotFields(`?farm_id__id=${e.target.value}`)); // read all  pivot field using farm ID
              dispatch(readPivotGeoFields(Number(e.target.value)));
            }

            let farmGeo = await navigationServices.getGboundry(
              selectedFarm?.data?.gboundary_id ?? null
            );
            if (farmGeo) {
              dispatch(
                setFarmData({
                  key: "geo_farm",
                  value: farmGeo?.data,
                })
              );
            }
          }}
          options={farms
            ?.filter((farm) => farm.farming_type === currentType)
            ?.map?.((farm) => ({
              label: farm.farm_name,
              value: farm.id,
            }))}
          {...props}
        />
        <Select
          label={t("Select Field")}
          name="object_id"
          options={[
            ...fields.map((geo_f) => {
              return { value: geo_f.id, label: geo_f.field_code };
            }),
          ]}
          onChange={async (e) => {
            if (e.target.value !== "allFields") {
              let curField = await fields?.find?.(
                (ffield) => String(ffield.id) == String(e.target.value)
              );
              let geoField = await geo_fields?.find?.(
                (ffield) => String(ffield.id) == String(e.target.value)
              );
              if (currentType === "tree") {
                dispatch(
                  setTreeFieldData({
                    key: "field",
                    value: curField,
                  })
                );
                dispatch(
                  setTreeFieldData({
                    key: "geo_field",
                    value: geoField,
                  })
                );
              } else if (currentType === "crop") {
                dispatch(
                  setCropFieldData({
                    key: "field",
                    value: curField,
                  })
                );
                dispatch(
                  setCropFieldData({
                    key: "geo_field",
                    value: geoField,
                  })
                );
              }
            }
          }}
          {...props}
        />
        <Input
          name="attributes_value.flow_rate_m3_per_h"
          label={t("Flow_Rate_m3/h")}
          type="number"
          {...props}
        />
        <Select
          name="supplier_id"
          label={t("Supplier")}
          {...props}
          options={suppliers?.results?.map?.((user) => ({
            label: user.supplier_name,
            value: user.id,
          }))}
        />
        <DateInput
          name="installation_date"
          label={t("Installation Date")}
          {...props}
        />
        <Range
          name="attributes_value.efficiency"
          label={t("Efficiency")}
          min={0}
          max={100}
          {...props}
        />
      </div>

      <Button
        title={t("Save & Continue")}
        className="w-full h-[35px] text-white rounded-lg bg-main  text-md mt-5"
      />
    </>
  );
}

export default pumpForm;
